/* Libraries */
import React, { FC, useEffect, useRef, useState, useMemo } from 'react';
import { snakeCase } from 'lodash';
import { Instance } from 'mobx-state-tree';
import { useHistory, useLocation } from 'react-router-dom';

/* Hooks */
import useMst from '~/src/hooks/useMst';
import usePageScroll from '~/src/hooks/usePageScroll';
import useTemplatesPagination from '~/src/hooks/useTemplatesPagination';
import useFeatureAccesses from '~/src/hooks/useFeatureAccesses';
import useClioWorkCopy from '~/src/components/WorkTemplateMigration/useClioWorkCopy';
import { useLayoutContext } from '~/src/contexts';

/* Utils */
import { isTransferredAndNewerThan7Days } from './utils';

/* Components */
import Templates from './Templates';
import TemplatesEmptyView from './TemplatesEmptyView';
import Breadcrumb from '~/src/components/Breadcrumb';
import ClioWorkCopyControls, {
  ClioWorkCopyTab,
  tabOf,
} from '~/src/components/WorkTemplateMigration/ClioWorkCopyControls';
import { LAYOUT_MODAL_TYPES } from '~/src/components/PageLayout/Modals';

/* Types */
import { SortByOrder } from '~/src/hooks/sortByOrder';
import { TemplateStore } from '~/src/stores/templateStore';
import ClioTemplatesSection from './ClioTemplatesSection';
import { useCurrentOrg, useCurrentOrgFprint } from '~/src/entities/user';
import type { ClioWorkCopyState } from '~/src/services/api/clioService';
import { TemplateSource } from '~/src/types/template';
import { getParentRoute } from '~/src/utils/strings';
import {
  CUSTOM_TEMPLATES_STRING,
  GENERAL_TEMPLATE_FILTER,
  CLIO_TEMPLATES_STRING,
  SORT_ORDER_ENUM,
} from './constants';
import { useCheckClioTemplatesExist } from '~/src/entities/template/hooks/useCheckClioTemplatesExist';
import { BreadcrumbDataItem } from '../Breadcrumb/Breadcrumb';

export interface Template extends Instance<typeof TemplateStore> {}

interface Actions {
  rename?: (templateSet: Template) => void;
  delete?: (templateSet: Template) => void;
  preview?: (templateSet: Template) => void;
}

interface Props {
  addTemplateToSeletionSidebar: (t: Template) => void;
  rowActions?: Actions;
  hideDraftButton?: boolean;
  onPreviewTemplate?: (t: Template) => void;
  source?: TemplateSource;
  // Lets an admin copy the firm's templates to Clio Work from this table (the Library only).
  enableClioWorkCopy?: boolean;
}

// A template in Clio Work can be copied again only once changes are made to it, since copying
// it adds a new version there: an unchanged one would add a version identical to the last.
const isSelectable = (state: ClioWorkCopyState) =>
  state.status === 'not_copied' ||
  (state.status === 'in_clio_work' && state.out_of_sync) ||
  (state.status === 'failed' && state.can_retry);

// Not in Clio Work yet, and copyable.
const isRemaining = (state: ClioWorkCopyState) =>
  isSelectable(state) && state.status !== 'in_clio_work';

const pluralTemplates = (count: number) =>
  `${count} ${count === 1 ? 'template' : 'templates'}`;

// The Library shows this many templates, suggested first, until the user asks for all of them.
export const PREVIEW_ROWS = 10;

const TemplatesWrapper: FC<Props> = ({
  addTemplateToSeletionSidebar,
  rowActions,
  hideDraftButton = false,
  onPreviewTemplate,
  source = TemplateSource.FIRM,
  enableClioWorkCopy = false,
}) => {
  const location = useLocation();
  const history = useHistory();
  const parentRoute = getParentRoute(location.pathname);

  const searchParams = new URLSearchParams(location.search);
  const practiceAreaFilter = searchParams.get('practiceArea');

  const { getSelectedTemplates } = useMst((store) => ({
    getSelectedTemplates: store.templates.getSelectedTemplates,
  }));

  // Local state for status priority sorting
  const [localSortBy, setLocalSortBy] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<SORT_ORDER_ENUM>(
    SORT_ORDER_ENUM.UNSORTED,
  );

  const {
    isPracticeAreaStandardContentEnabled,
    isDraftToWorkTemplateMigrationEnabled,
  } = useFeatureAccesses();
  const { org } = useCurrentOrg();
  const orgFprint = useCurrentOrgFprint();
  const { showModal, hideModal } = useLayoutContext();

  const clioWorkCopyActive =
    enableClioWorkCopy &&
    source === TemplateSource.FIRM &&
    !!org &&
    !!isDraftToWorkTemplateMigrationEnabled;
  // The done email links to its copy ("?copyJob="): the Library opens on just the templates that
  // copy copied or couldn't copy, until the user asks for all of them, searches or picks another
  // tab.
  const [copyJobId, setCopyJobId] = useState<number | null>(
    () => Number(searchParams.get('copyJob')) || null,
  );
  const clioWorkCopy = useClioWorkCopy(
    orgFprint,
    clioWorkCopyActive,
    copyJobId,
  );
  const [copyTab, setCopyTab] = useState<ClioWorkCopyTab>('all');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showAllRows, setShowAllRows] = useState(false);
  // A column the user sorted by replaces the suggested-first order.
  const [userSorted, setUserSorted] = useState(false);
  // The Word add-in links to one template ("?templateId="): the Library opens on just that
  // template, ticked if it can be copied, until the user asks for all of them, searches or picks
  // another tab.
  const [focusedId, setFocusedId] = useState<number | null>(
    () => Number(searchParams.get('templateId')) || null,
  );
  const focusActive = clioWorkCopyActive && focusedId !== null;
  const focusTicked = useRef(false);
  const copyJobTemplateIds = clioWorkCopy.overview?.copy_job_template_ids;
  const copyJobActive =
    clioWorkCopyActive && copyJobId !== null && !!copyJobTemplateIds?.length;
  // The templates the link asked for: the add-in's template, or the email's copy.
  const linkedIds = useMemo(() => {
    if (focusActive && focusedId !== null) {
      return new Set([focusedId]);
    }
    return copyJobActive ? new Set(copyJobTemplateIds) : null;
  }, [focusActive, focusedId, copyJobActive, copyJobTemplateIds]);

  const copyStates = useMemo(
    () => Array.from(clioWorkCopy.statesById.values()),
    [clioWorkCopy.statesById],
  );
  const selectableIds = useMemo(
    () => new Set(copyStates.filter(isSelectable).map((t) => t.template_id)),
    [copyStates],
  );
  const remainingIds = useMemo(
    () => copyStates.filter(isRemaining).map((t) => t.template_id),
    [copyStates],
  );
  // Suggested templates that haven't been copied yet.
  const suggestedIds = useMemo(
    () =>
      copyStates
        .filter((t) => t.suggested && t.status === 'not_copied')
        .map((t) => t.template_id),
    [copyStates],
  );

  // A template stops being selectable once it starts copying.
  useEffect(() => {
    setSelectedIds((current) => {
      const kept = new Set(
        Array.from(current).filter((id) => selectableIds.has(id)),
      );
      return kept.size === current.size ? current : kept;
    });
  }, [selectableIds]);

  useEffect(() => {
    if (
      focusActive &&
      !focusTicked.current &&
      focusedId !== null &&
      selectableIds.has(focusedId)
    ) {
      focusTicked.current = true;
      setSelectedIds(new Set([focusedId]));
    }
  }, [focusActive, focusedId, selectableIds]);

  const clearFocus = () => {
    if (focusedId === null && copyJobId === null) {
      return;
    }
    setFocusedId(null);
    setCopyJobId(null);
    const params = new URLSearchParams(location.search);
    params.delete('templateId');
    params.delete('copyJob');
    history.replace({ search: params.toString() });
  };

  // Opened from the done email: until the overview says which templates are the copy's, show none
  // (the table shows it's loading) rather than every template and then cut the list down.
  const copyJobPending =
    clioWorkCopyActive &&
    copyJobId !== null &&
    !clioWorkCopy.overview &&
    !clioWorkCopy.loadFailed;

  // A copy the overview doesn't know for this firm, one that copied nothing, or an overview that
  // couldn't be loaded: show every template.
  useEffect(() => {
    if (
      clioWorkCopyActive &&
      copyJobId !== null &&
      ((clioWorkCopy.overview && !copyJobTemplateIds?.length) ||
        (!clioWorkCopy.overview && clioWorkCopy.loadFailed))
    ) {
      clearFocus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clioWorkCopyActive,
    copyJobId,
    clioWorkCopy.overview,
    clioWorkCopy.loadFailed,
    copyJobTemplateIds,
  ]);

  const toggleSelected = (id: number) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const confirmCopy = (templateIds: number[]) => {
    // One copy at a time: the next one waits for the running copy to end.
    if (clioWorkCopy.copying) {
      return;
    }
    const count = templateIds.length;
    // A template with a copy in Clio Work (one with changes made, or a retry of one whose earlier
    // copy is still there) gets a new version of that copy, which overrides edits made to it in
    // Clio Work. Even one such template is worth the warning, so the batch gets the short modal
    // that says so; a first copy of every template gets the one about where they'll go.
    const replacesACopy = templateIds.some((id) => {
      const state = clioWorkCopy.statesById.get(id);
      return (
        state?.status === 'in_clio_work' ||
        (state?.status === 'failed' && !!state.document_id)
      );
    });
    const message = replacesACopy ? (
      <span className="block w-[480px] max-w-full">
        <span className="block">
          Previously migrated templates in Clio Work will be updated and a new
          version will be created.
        </span>
        <span
          role="alert"
          className="mt-3 flex items-start rounded border border-yellow-300 bg-yellow-50 p-3 text-yellow-800"
        >
          <span aria-hidden="true" className="mr-2">
            ⚠
          </span>
          <span>
            Any changes made to the existing documents in Clio Work will be
            overridden.
          </span>
        </span>
      </span>
    ) : (
      <span className="block w-[480px] max-w-full">
        <strong className="block">{"Where they'll go"}</strong>
        <span className="block mb-3">
          {`${
            count === 1 ? 'This template' : 'These templates'
          } will be saved into a Clio Draft folder in your documents. Everyone at your firm can use ${
            count === 1 ? 'it' : 'them'
          }.`}
        </span>
        <strong className="block">{"While they're copying"}</strong>
        <span className="block mb-3">
          {
            "Your templates in Clio Draft will work as usual. Longer templates can take a few minutes. Any edits made to the template in Word while it's being copied across will not be copied."
          }
        </span>
        <strong className="block">{"After they're copied"}</strong>
        <span className="block">
          {
            'Make any changes in Clio Work. You can still edit the template in Word, but these changes will not be automatically copied across to the template in Clio Work.'
          }
        </span>
      </span>
    );
    showModal(LAYOUT_MODAL_TYPES.confirm, {
      title: `Copy ${pluralTemplates(count)} to Clio Work`,
      message,
      primaryActionTitle: `Copy ${pluralTemplates(count)}`,
      onConfirm: async () => {
        hideModal();
        if (await clioWorkCopy.start(templateIds)) {
          setSelectedIds(new Set());
        }
      },
    });
  };
  const getClioTemplatesParams: {
    clioTemplates?: string;
    practiceArea?: string;
    jurisdiction?: string;
  } = {};
  if (isPracticeAreaStandardContentEnabled) {
    if (source === TemplateSource.Clio) {
      if (practiceAreaFilter === GENERAL_TEMPLATE_FILTER) {
        getClioTemplatesParams.clioTemplates = 'general';
      } else {
        getClioTemplatesParams.clioTemplates = 'only';
      }
    } else {
      getClioTemplatesParams.clioTemplates = 'exclude';
    }
    if (practiceAreaFilter && practiceAreaFilter !== GENERAL_TEMPLATE_FILTER) {
      // viewing clio templates for a practice area
      getClioTemplatesParams.practiceArea = practiceAreaFilter;
      getClioTemplatesParams.jurisdiction = org?.primary_state;
    }
  }
  const {
    next,
    loading,
    empty,
    done,
    data,
    setSearch,
    setSortBy,
    setOrder,
    reload,
  } = useTemplatesPagination(getClioTemplatesParams);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const pageScroll = usePageScroll();

  const loadMore = next;

  const handleOnPageScroll = () => {
    if ((pageScroll === 0 || pageScroll >= 85) && !loading) {
      loadMore();
    }
  };
  const { data: clioTemplatesExist } = useCheckClioTemplatesExist();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleOnPageScroll, [pageScroll]);

  // A copy-status tab other than "All" filters the loaded rows by copy state, so it needs every
  // page loaded. "All" pages as the user scrolls, like the rest of the Library: its preview and its
  // failed-first and suggested-first order work on the rows loaded so far.
  useEffect(() => {
    if (clioWorkCopyActive && copyTab !== 'all' && !done && !loading) {
      loadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clioWorkCopyActive, copyTab, done, loading]);

  // The linked templates may not all be on the pages loaded so far.
  const loadedLinkedCount = linkedIds
    ? data.filter((template: Template) => linkedIds.has(Number(template.id)))
        .length
    : 0;
  const linkedRowsLoaded = !!linkedIds && loadedLinkedCount === linkedIds.size;
  useEffect(() => {
    if (!linkedIds || linkedRowsLoaded || loading) {
      return;
    }
    if (!done) {
      loadMore();
    } else if (loadedLinkedCount === 0) {
      // None is among the firm's templates (e.g. deleted since): show them all instead.
      clearFocus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedIds, linkedRowsLoaded, loadedLinkedCount, done, loading]);

  const rawTableData = data.map((template: Template) => ({
    id: template.id,
    title: template.title,
    templateSource: template.templateSource,
    documentCategoryId: template.documentType,
    updatedAt: template.updatedAt,
    createdAt: template.createdAt,
    selectedState: { selected: template.selected, id: template.id },
    status: template.status,
    statusPriority: isTransferredAndNewerThan7Days(template)
      ? 'A_new'
      : `B_${template.status}`,
  }));

  // Apply local sorting for statusPriority
  const tableData = useMemo(() => {
    if (
      localSortBy === 'statusPriority' &&
      sortOrder !== SORT_ORDER_ENUM.UNSORTED
    ) {
      return [...rawTableData].sort((a, b) => {
        const aValue = a.statusPriority;
        const bValue = b.statusPriority;

        if (aValue < bValue) {
          return sortOrder;
        }
        if (aValue > bValue) {
          return -sortOrder;
        }
        return 0;
      });
    }
    return rawTableData;
  }, [rawTableData, localSortBy, sortOrder]);

  const visibleTableData = useMemo(() => {
    if (!clioWorkCopyActive) {
      return tableData;
    }
    if (copyJobPending) {
      return [];
    }
    if (linkedIds) {
      return tableData.filter((row: { id: string }) =>
        linkedIds.has(Number(row.id)),
      );
    }
    const rows =
      copyTab === 'all'
        ? tableData
        : tableData.filter((row: { id: string }) => {
            const state = clioWorkCopy.statesById.get(Number(row.id));
            return !!state && tabOf(state) === copyTab;
          });
    // Templates that failed to copy come first so they get seen, then (unless the user sorted
    // by a column) the suggested ones.
    const rank = (row: { id: string }) => {
      const state = clioWorkCopy.statesById.get(Number(row.id));
      if (state?.status === 'failed') {
        return 0;
      }
      return !userSorted && state?.suggested ? 1 : 2;
    };
    type Row = (typeof rows)[number];
    return rows
      .map((row: Row, index: number) => ({ row, index }))
      .sort(
        (a: { row: Row; index: number }, b: { row: Row; index: number }) =>
          rank(a.row) - rank(b.row) || a.index - b.index,
      )
      .map(({ row }: { row: Row }) => row);
  }, [
    tableData,
    clioWorkCopyActive,
    copyJobPending,
    linkedIds,
    clioWorkCopy.statesById,
    copyTab,
    userSorted,
  ]);

  // The first rows only, on the "All" tab, until the user asks for all of them or searches.
  const previewed =
    clioWorkCopyActive &&
    !linkedIds &&
    !showAllRows &&
    copyTab === 'all' &&
    !userSorted &&
    visibleTableData.length > PREVIEW_ROWS;
  const shownTableData = previewed
    ? visibleTableData.slice(0, PREVIEW_ROWS)
    : visibleTableData;

  const handleSearchChange = (searchValue: string) => {
    setSearch(searchValue);
    if (searchValue) {
      setShowAllRows(true);
      clearFocus();
    }
  };

  const handleSortByChange = (property: string, order: number) => {
    setUserSorted(!!property);
    if (!property) {
      // Reset both remote and local sorting
      setLocalSortBy('');
      setSortOrder(SORT_ORDER_ENUM.UNSORTED);
      return;
    }

    // Handle statusPriority sorting locally (client-side)
    if (property === 'statusPriority') {
      setLocalSortBy(property);
      setSortOrder(
        order > 0 ? SORT_ORDER_ENUM.ASCENDING : SORT_ORDER_ENUM.DESCENDING,
      );
      return;
    }

    // Reset local sorting when sorting other columns
    setLocalSortBy('');
    setSortOrder(SORT_ORDER_ENUM.UNSORTED);

    const orderLabel = order > 0 ? SortByOrder.desc : SortByOrder.asc;
    let sortBy = snakeCase(property);

    if (property === 'updatedAt') {
      sortBy = 'last_modified';
    }

    setSortBy(sortBy);
    setOrder(orderLabel);
  };

  if (empty && source === TemplateSource.FIRM) {
    return (
      <>
        <TemplatesEmptyView />
      </>
    );
  }

  const selectedTemplateIds = getSelectedTemplates().map(
    (template: Template) => template.id,
  );

  const primaryPracticeArea = org?.primary_practice_area;
  const secondaryPracticeArea = org?.secondary_practice_area;
  const primaryState = org?.primary_state;

  const breadcrumbData: BreadcrumbDataItem[] = [
    {
      label: 'All Word templates',
      url: parentRoute,
    },
    {
      label: CLIO_TEMPLATES_STRING,
    },
  ];

  let tableTitle: string;
  if (practiceAreaFilter && primaryState) {
    if (practiceAreaFilter === GENERAL_TEMPLATE_FILTER) {
      tableTitle = 'General';
    } else {
      tableTitle = `${primaryState} - ${practiceAreaFilter}`;
    }
  } else {
    tableTitle = CUSTOM_TEMPLATES_STRING;
  }

  return (
    <>
      {isPracticeAreaStandardContentEnabled &&
        source === TemplateSource.Clio && <Breadcrumb data={breadcrumbData} />}
      {isPracticeAreaStandardContentEnabled &&
        source === TemplateSource.FIRM &&
        clioTemplatesExist && (
          <>
            <ClioTemplatesSection
              primaryPracticeArea={primaryPracticeArea}
              secondaryPracticeArea={secondaryPracticeArea}
              primaryState={primaryState}
            />
          </>
        )}
      <Templates
        templates={shownTableData}
        visibleItems={shownTableData.length}
        empty={
          !loading && !copyJobPending && done && shownTableData.length === 0
        }
        rowActions={rowActions}
        onAddTemplateToSeletionSidebar={addTemplateToSeletionSidebar}
        selectedTemplateIds={selectedTemplateIds}
        onSearchChange={handleSearchChange}
        onSortByChange={handleSortByChange}
        hideDraftButton={hideDraftButton}
        onPreviewTemplate={onPreviewTemplate}
        cardTitle={tableTitle}
        loading={loading || copyJobPending}
        done={done}
        onLoadMore={loadMore}
        banner={
          clioWorkCopyActive && clioWorkCopy.overview ? (
            <>
              <ClioWorkCopyControls
                orgFprint={orgFprint}
                states={copyStates}
                latestJob={clioWorkCopy.overview.latest_job}
                clioDraftFolderId={clioWorkCopy.overview.clio_draft_folder_id}
                copying={clioWorkCopy.copying}
                tab={copyTab}
                onTabChange={(tab: ClioWorkCopyTab) => {
                  clearFocus();
                  setCopyTab(tab);
                }}
                remainingCount={remainingIds.length}
                onCopyAll={() => confirmCopy(remainingIds)}
                suggestedCount={suggestedIds.length}
                onCopySuggested={() => confirmCopy(suggestedIds)}
                onSelectRemaining={() => setSelectedIds(new Set(remainingIds))}
                onSelectOutOfSync={() =>
                  setSelectedIds(
                    new Set(
                      clioWorkCopy.overview?.latest_job?.changed_template_ids ??
                        [],
                    ),
                  )
                }
                onRetry={confirmCopy}
                // The banner is about the firm's latest copy: in a view of linked templates it
                // wouldn't match the list, and its buttons act on templates that aren't shown.
                copyingOnly={!!linkedIds}
              />
              {linkedIds && (
                <div className="mt-3 flex items-center text-sm">
                  <span>
                    {focusActive
                      ? 'Showing only the template you opened in Word.'
                      : `Showing only the ${pluralTemplates(
                          // Until every page is loaded, the copy counts its templates.
                          linkedRowsLoaded || done
                            ? loadedLinkedCount
                            : linkedIds.size,
                        )} from your migration.`}
                  </span>
                  <button
                    type="button"
                    className="ml-2 text-blue-700"
                    onClick={() => {
                      clearFocus();
                      // Every template, not the first rows the Library otherwise shows.
                      setShowAllRows(true);
                    }}
                  >
                    Show all templates
                  </button>
                </div>
              )}
            </>
          ) : undefined
        }
        clioWorkCopy={
          clioWorkCopyActive
            ? {
                statesById: clioWorkCopy.statesById,
                selectableIds,
                selectedIds,
                folderId: clioWorkCopy.overview?.clio_draft_folder_id ?? null,
                onToggle: toggleSelected,
                onSelectAll: () => setSelectedIds(new Set(selectableIds)),
                onClear: () => setSelectedIds(new Set()),
                onCopySelected: () => confirmCopy(Array.from(selectedIds)),
                onRetry: confirmCopy,
                copying: clioWorkCopy.copying,
                preview: previewed
                  ? {
                      shown: PREVIEW_ROWS,
                      // Until every page is loaded, the copy states count the templates.
                      total: done
                        ? visibleTableData.length
                        : Math.max(visibleTableData.length, copyStates.length),
                      onShowAll: () => setShowAllRows(true),
                    }
                  : undefined,
              }
            : undefined
        }
      />
    </>
  );
};

export default TemplatesWrapper;
